import { useEffect, useMemo } from "react";
import { useQuery, useLazyQuery, QueryHookOptions } from "@apollo/client";
import gql from "graphql-tag";
import { INTERNAL_PREV_SEASON } from "@ugg/shared/utils/season-helpers";

interface LPTimelineDataPoint {
  champion_id?: number;
  diff?: number;
  lp: number;
  match_creation_time?: string;
  match_id?: number;
  rank: string;
  score: number;
  tier: string;
}

export interface YearInReviewData {
  // --- Aram Statistics ---
  aram_data: {
    assists: { avg: number; max: number };
    avg_cs: number;
    avg_gold: number;
    kills: { avg: number };
    deaths: { avg: number };
    cs_per_min: { avg: number; max: number };
    damage_share: { avg: number; max: number };
    gold_per_min: { avg: number; max: number };
    gold_share: { avg: number; max: number };
    kda: { avg: number; max: number };
    losses: number;
    wins: number;
    time_played: number;
    total_games: number;
    fastest_tower: number;
    time_dead: number;
    // -- aram champion data -- //
    champion_data: {
      [championId: string]: {
        assists: { avg: number; max: number };
        avg_cs: number;
        avg_gold: number;
        champs_killed: { [champId: string]: number };
        champs_killed_by: { [champId: string]: number };
        champs_lost_against: { [champId: string]: number };
        champs_won_against: { [champId: string]: number };
        cs_per_min: { avg: number; max: number };
        damage_share: { avg: number; max: number };
        deaths: { avg: number; max: number };
        gold_per_min: { avg: number; max: number };
        gold_share: { avg: number; max: number };
        kda: { avg: number; max: number };
        kills: { avg: number; max: number };
        losses: number;
        multikills: {
          avg: {
            double_kills: number;
            penta_kills: number;
            quadra_kills: number;
            triple_kills: number;
          };
          max: {
            double_kills: number;
            penta_kills: number;
            quadra_kills: number;
            triple_kills: number;
          };
        };
        time_dead: number;
        time_played: number;
        total_games: number;
        wins: number;
      };
    };
    // -- aram multikills --
    multikills: {
      avg: {
        double_kills: number;
        penta_kills: number;
        quadra_kills: number;
        triple_kills: number;
      };
      max: {
        double_kills: number;
        penta_kills: number;
        quadra_kills: number;
        triple_kills: number;
      };
    };
    // -- aram game logs --
    game_logs: {
      [date: string]: {
        date: string;
        games_played: number;
        losses: number;
        lp_gain: number;
        wins: number;
      };
    };
    // -- aram pings --
    pings: {
      avg: {
        all_in_pings: number;
        assist_me_pings: number;
        command_pings: number;
        enemy_vision_pings: number;
        get_back_pings: number;
        hold_pings: number;
        need_vision_pings: number;
        on_my_way_pings: number;
        push_pings: number;
        question_pings: number;
        total_pings: number;
        vision_cleared_pings: number;
      };
      max: {
        all_in_pings: number;
        assist_me_pings: number;
        command_pings: number;
        enemy_vision_pings: number;
        get_back_pings: number;
        hold_pings: number;
        need_vision_pings: number;
        on_my_way_pings: number;
        push_pings: number;
        question_pings: number;
        total_pings: number;
        vision_cleared_pings: number;
      };
    };
  };
  // --- Top-Level Aggregate Statistics ---
  assists: { avg: number; max: number };
  avg_cs: number;
  avg_gold: number;
  kills: { avg: number };
  deaths: { avg: number };
  cs_per_min: { avg: number; max: number };
  damage_share: { avg: number; max: number };
  gold_per_min: { avg: number; max: number };
  gold_share: { avg: number; max: number };
  kda: { avg: number; max: number };
  losses: number;
  wins: number;
  time_played: number;
  total_games: number;

  // --- Objectives and Timers ---
  fastest_dragon: number;
  fastest_tower: number;

  objectives: {
    avg: {
      atakhan_kills: number;
      baron_kills: number;
      dragons_killed: number;
      grub_kills: number;
      herald_kills: number;
      tower_kills: number;
    };
    max: {
      atakhan_kills: number;
      baron_kills: number;
      dragons_killed: number;
      grub_kills: number;
      herald_kills: number;
      tower_kills: number;
    };
  };

  // --- Wards and Vision ---
  wards: {
    avg: {
      blue_wards_placed: number;
      control_wards_placed: number;
      normal_wards_placed: number;
      total_wards_placed: number;
      vision_score: number;
    };
    max: {
      blue_wards_placed: number;
      control_wards_placed: number;
      normal_wards_placed: number;
      total_wards_placed: number;
      vision_score: number;
    };
  };

  // --- Multikills (Averages and Maxes) ---
  multikills: {
    avg: {
      double_kills: number;
      penta_kills: number;
      quadra_kills: number;
      triple_kills: number;
    };
    max: {
      double_kills: number;
      penta_kills: number;
      quadra_kills: number;
      triple_kills: number;
    };
  };

  // --- Game Logs (for PlayTimeChart) ---
  game_logs: {
    [date: string]: {
      date: string;
      games_played: number;
      losses: number;
      lp_gain: number;
      wins: number;
    };
  };

  // --- LP Timeline ---
  lp_timeline: LPTimelineDataPoint[];

  // --- Pings ---
  pings: {
    avg: {
      all_in_pings: number;
      assist_me_pings: number;
      command_pings: number;
      enemy_vision_pings: number;
      get_back_pings: number;
      hold_pings: number;
      need_vision_pings: number;
      on_my_way_pings: number;
      push_pings: number;
      question_pings: number;
      total_pings: number;
      vision_cleared_pings: number;
    };
    max: {
      all_in_pings: number;
      assist_me_pings: number;
      command_pings: number;
      enemy_vision_pings: number;
      get_back_pings: number;
      hold_pings: number;
      need_vision_pings: number;
      on_my_way_pings: number;
      push_pings: number;
      question_pings: number;
      total_pings: number;
      vision_cleared_pings: number;
    };
  };

  // --- Champion Data (Nested by Champion ID) ---
  champion_data: {
    [championId: string]: {
      assists: { avg: number; max: number };
      avg_cs: number;
      avg_gold: number;
      champs_killed: { [champId: string]: number };
      champs_killed_by: { [champId: string]: number };
      champs_lost_against: { [champId: string]: number };
      champs_won_against: { [champId: string]: number };
      cs_per_min: { avg: number; max: number };
      damage_share: { avg: number; max: number };
      deaths: { avg: number; max: number };
      gold_per_min: { avg: number; max: number };
      gold_share: { avg: number; max: number };
      kda: { avg: number; max: number };
      kills: { avg: number; max: number };
      losses: number;
      multikills: {
        avg: {
          double_kills: number;
          penta_kills: number;
          quadra_kills: number;
          triple_kills: number;
        };
        max: {
          double_kills: number;
          penta_kills: number;
          quadra_kills: number;
          triple_kills: number;
        };
      };
      time_dead: number;
      time_played: number;
      total_games: number;
      wins: number;
    };
  };

  // --- Role Data ---
  role_data: {
    [role: string]: {
      assists: { avg: number; max: number };
      avg_cs: number;
      avg_gold: number;
      cs_per_min: { avg: number; max: number };
      damage_dealt: number;
      damage_share: { avg: number; max: number };
      deaths: { avg: number; max: number };
      dmg_mitigated: number;
      gold_per_min: { avg: number; max: number };
      gold_share: { avg: number; max: number };
      healing: number;
      kda: { avg: number; max: number };
      kills: { avg: number; max: number };
      losses: number;
      time_dead: number;
      time_played: number;
      total_games: number;
      wins: number;
    };
  };

  // --- Friend and Enemy Data ---
  friend_data: Array<{
    losses: number;
    lp_gain: number;
    profile_icon_id: number;
    riot_tag_line: string;
    riot_user_name: string;
    time_played: number;
    total_games: number;
    wins: number;
  }>;
  enemy_data: Array<{
    losses: number;
    profile_icon_id: number;
    riot_tag_line: string;
    riot_user_name: string;
    time_played: number;
    total_games: number;
    wins: number;
  }>;
}

export enum YIRState {
  IN_QUEUE = "In Queue",
  PROCESSING = "Processing",
  NO_DATA = "No data",
  GENERATED = "Generated",
  NOT_STARTED = "Not Started or Invalid input.",
  NO_MATCHES = "No Matches",
  ERROR = "Error",
}

export interface YIRTest {
  yearInReviewStatusRequest: YIRState;
}
export interface YearInReviewResponse {
  data: YearInReviewData | null;
}

export interface YearInReviewQueryResponse {
  yearInReviewDataRequest: string;
}

export const GET_YEAR_IN_REVIEW = gql`
  query getYearInReview($regionId: String!, $riotUserName: String!, $riotTagLine: String!) {
    fetchYearInReview(regionId: $regionId, riotUserName: $riotUserName, riotTagLine: $riotTagLine) {
      status
      data {
        assists {
          avg
          max
        }
        avg_cs
        avg_gold
        kills {
          avg
        }
        deaths {
          avg
        }
        cs_per_min {
          avg
          max
        }
        damage_share {
          avg
          max
        }
        gold_per_min {
          avg
          max
        }
        gold_share {
          avg
          max
        }
        kda {
          avg
          max
        }
        losses
        wins
        time_played
        total_games
        fastest_dragon
        fastest_tower
        objectives {
          avg {
            atakhan_kills
            baron_kills
            dragons_killed
            grub_kills
            herald_kills
            tower_kills
          }
          max {
            atakhan_kills
            baron_kills
            dragons_killed
            grub_kills
            herald_kills
            tower_kills
          }
        }
        wards {
          avg {
            blue_wards_placed
            control_wards_placed
            normal_wards_placed
            total_wards_placed
            vision_score
          }
          max {
            blue_wards_placed
            control_wards_placed
            normal_wards_placed
            total_wards_placed
            vision_score
          }
        }
        multikills {
          avg {
            double_kills
            penta_kills
            quadra_kills
            triple_kills
          }
          max {
            double_kills
            penta_kills
            quadra_kills
            triple_kills
          }
        }
        game_logs
        lp_timeline {
          ranked_flex_sr {
            champion_id
            diff
            lp
            match_creation_time
            match_id
            rank
            score
            tier
          }
          ranked_solo_5x5 {
            champion_id
            diff
            lp
            match_creation_time
            match_id
            rank
            score
            tier
          }
        }
        pings {
          avg {
            all_in_pings
            assist_me_pings
            command_pings
            enemy_vision_pings
            get_back_pings
            hold_pings
            need_vision_pings
            on_my_way_pings
            push_pings
            question_pings
            total_pings
            vision_cleared_pings
          }
          max {
            all_in_pings
            assist_me_pings
            command_pings
            enemy_vision_pings
            get_back_pings
            hold_pings
            need_vision_pings
            on_my_way_pings
            push_pings
            question_pings
            total_pings
            vision_cleared_pings
          }
        }
        champion_data
        role_data
        friend_data {
          losses
          lp_gain
          profile_icon_id
          riot_tag_line
          riot_user_name
          time_played
          total_games
          wins
        }
        enemy_data {
          losses
          profile_icon_id
          riot_tag_line
          riot_user_name
          time_played
          total_games
          wins
        }
      }
    }
  }
`;

const POLLING_INTERVAL = 5000;

export const GET_USER_YEAR_IN_REVIEW = gql`
  query ($assignedSeason: String!, $riot_username: String!, $riot_tagline: String!, $regionId: String!) {
    yearInReviewDataRequest(
      riot_username: $riot_username
      riot_tagline: $riot_tagline
      regionId: $regionId
      assignedSeason: $assignedSeason
    )
  }
`;

export function getUserYearInReviewData(
  riot_username: string,
  riot_tagline: string,
  regionId: string,
  assignedSeason?: string,
  queryOptions: QueryHookOptions = {},
) {
  const { data, loading, error, ...rest } = useQuery<YearInReviewQueryResponse>(GET_USER_YEAR_IN_REVIEW, {
    ...queryOptions,
    variables: {
      riot_username,
      riot_tagline,
      regionId,
      assignedSeason: String(assignedSeason || INTERNAL_PREV_SEASON),
    },
  });

  const parsedData = useMemo((): YearInReviewData | null => {
    if (!data?.yearInReviewDataRequest) return null;
    try {
      return JSON.parse(data.yearInReviewDataRequest) as YearInReviewData;
    } catch {
      return null;
    }
  }, [data?.yearInReviewDataRequest]);

  return { data: parsedData, loading, error, ...rest };
}

export const GET_USER_YEAR_IN_REVIEW_STATUS = gql`
  query ($assignedSeason: String!, $riot_username: String!, $riot_tagline: String!, $regionId: String!) {
    yearInReviewStatusRequest(
      assignedSeason: $assignedSeason
      riot_username: $riot_username
      riot_tagline: $riot_tagline
      regionId: $regionId
    )
  }
`;

export function getUserYearInReviewStatus(
  riot_username: string,
  riot_tagline: string,
  regionId: string,
  assignedSeason?: string,
  queryOptions: QueryHookOptions = {},
) {
  const apiParams = {
    riot_username,
    riot_tagline,
    regionId,
    assignedSeason: String(assignedSeason || INTERNAL_PREV_SEASON),
  };

  const {
    data: yearInReviewStatus,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery<YIRTest>(GET_USER_YEAR_IN_REVIEW_STATUS, {
    ...queryOptions,
    variables: apiParams,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (
      yearInReviewStatus?.yearInReviewStatusRequest === YIRState.IN_QUEUE ||
      yearInReviewStatus?.yearInReviewStatusRequest === YIRState.PROCESSING
    ) {
      startPolling(POLLING_INTERVAL);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [yearInReviewStatus?.yearInReviewStatusRequest, startPolling, stopPolling]);

  return { data: yearInReviewStatus?.yearInReviewStatusRequest, loading, error, refetch };
}

// THIS REQUIRES A LOGIN AND A BEARER TOKEN FROM THE LOGIN NOT SURE HOW TO IMPLEMENT ON THE FRONT END
export const YEAR_INREVIEW_SUBMISSION = gql`
  query ($season: Int!) {
    yearInReviewSubmission(season: $season) {
      success
    }
  }
`;

export function useInsertUserIntoYirQueue(season?: number) {
  const [insertIntoQueue, { data, loading, error }] = useLazyQuery(YEAR_INREVIEW_SUBMISSION, {
    variables: {
      season: season || parseInt(String(INTERNAL_PREV_SEASON)),
    },
    // Prevent it from running immediately if any variables change
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
  });

  return { insertIntoQueue, data, loading, error };
}
